import { fetchProduct } from '@/modules/product/api/singleProduct';
import { FetchClient, storeClient } from '@/shared/api/client';
import { SeoResponse } from '@/shared/api/types';

import {
  BasketCrossResponse,
  Cart,
  CreateOrder,
  CreateOrderResponse,
  DhlPickupPointByGeoPayload,
  DhlPickupPointResponse,
  FreeShippingInformation,
  InpostItem,
  MiniBasket,
  OlzaItem,
  OpenStreetMapGeocodeResponse,
  OrderStatus,
  OrlenPaczkaItem,
  PacketaClusterItem,
  PacketaPointItem,
  PacketaSearch,
  PaymentMethod,
  PickupPointPayload,
  PickupPointResponse,
  ShippingMethod,
  ShippingPoint,
  VatOSSCheck,
} from './types';

export const BASKET_ID_COOKIE_NAME = 'finesio_basket_id';

export const fetchFreeShippingInformation = async (
  fetch?: FetchClient
): Promise<FreeShippingInformation | null> => {
  try {
    return await storeClient<FreeShippingInformation>(
      `/basket/free-shipping`,
      {},
      fetch
    );
  } catch (e) {
    return null;
  }
};

export const fetchMiniBasket = async (
  basketId: string,
  fetch?: FetchClient
): Promise<MiniBasket | null> => {
  try {
    // const miniBasket = await storeClient<MiniBasket>(
    //   `/basket/get-mini`,
    //   {
    //     method: 'POST',
    //     body: {
    //       basketId,
    //     },
    //   },
    //   fetch
    // ).then((basket) => (basket.status === 'ordered' ? null : basket));

    const miniBasket = await storeClient<Cart>(
      `/basket/get`,
      {
        method: 'POST',
        body: {
          basketId,
        },
      },
      fetch
    ).then((cart) => cart.basket);
    if (miniBasket === null) return null;

    const products = await Promise.all(
      miniBasket.items.map(async (item) => {
        return await fetchProduct(item.productSlug, fetch);
      })
    );

    return {
      ...miniBasket,
      items: miniBasket.items.map((item) => ({
        ...item,
        product: products.find(
          (product) => product?.productId === item.productId
        ),
      })),
    };
  } catch (e) {
    return null;
  }
};

export const fetchCart = async (
  basketId: string,
  fetch?: FetchClient
): Promise<Cart | null> => {
  try {
    const response = await storeClient<Cart>(
      `/basket/get`,
      {
        method: 'POST',
        body: {
          basketId,
        },
      },
      fetch
    );

    const products = await Promise.all(
      response.basket.items.map(async (item) => {
        return await fetchProduct(item.productSlug, fetch);
      })
    );

    return {
      ...response,
      basket: {
        ...response.basket,
        items: response.basket.items.map((item) => ({
          ...item,
          product: products.find(
            (product) => product?.productId === item.productId
          ),
        })),
      },
    };
  } catch (e) {
    return null;
  }
};

export const createBasketWithProduct = async (
  body: {
    productId: string;
    quantity: number;
  },
  fetch?: FetchClient
): Promise<string | null> => {
  try {
    const result = await storeClient<{ basketId?: string }>(
      `/basket/add-with-product`,
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return result?.basketId || null;
  } catch (e) {
    return null;
  }
};

export const addItemToBasket = async (
  body: {
    basketId: string;
    productId: string;
    quantity: number;
  },
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    const result = await storeClient(
      `/basket/add-product`,
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return Boolean(result);
  } catch (e) {
    return false;
  }
};

export const removeItemFromBasket = async (
  body: {
    basketId: string;
    productId: string;
  },
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    const result = await storeClient(
      `/basket/remove-product`,
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return Boolean(result);
  } catch (e) {
    return false;
  }
};

export const changeItemQuantityInBasket = async (
  body: {
    basketId: string;
    productId: string;
    quantity: number;
  },
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    const result = await storeClient(
      `/basket/change-quantity`,
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return Boolean(result);
  } catch (e) {
    return false;
  }
};

export const changeShippingMethodInBasket = async (
  body: {
    basketId: string;
    shippingCode: string;
    shippingPoint?: ShippingPoint;
  },
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    const result = await storeClient(
      `/basket/change-shipping-method`,
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return Boolean(result);
  } catch (e) {
    return false;
  }
};

export const changePaymentMethodInBasket = async (
  body: {
    basketId: string;
    paymentCode: string;
  },
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    const result = await storeClient(
      `/basket/change-payment-method`,
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return Boolean(result);
  } catch (e) {
    return false;
  }
};

export const getAvailableShippingMethods = async (
  fetch?: FetchClient
): Promise<ShippingMethod[]> => {
  try {
    return await storeClient(`/shipping/list`, {}, fetch);
  } catch (e) {
    return [];
  }
};

export const getAvailablePaymentMethods = async (
  fetch?: FetchClient
): Promise<PaymentMethod[]> => {
  try {
    return await storeClient(`/payment/list`, {}, fetch);
  } catch (e) {
    return [];
  }
};

export const createOrder = async (
  body: CreateOrder,
  fetch?: FetchClient
): Promise<null | CreateOrderResponse> => {
  try {
    const result = await storeClient<CreateOrderResponse>(
      '/order/create',
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return result;
  } catch (e) {
    return null;
  }
};

export async function fetchOlzaPoints(
  country: OlzaItem['country'],
  speditions: string[],
  options?: {
    bounds?: [number, number, number, number];
    search?: string;
  }
): Promise<OlzaItem[]> {
  try {
    const result = await storeClient<{
      data: {
        items: OlzaItem[];
      };
    }>(
      `https://ppapi.olzalogistic.net/v1/pp/find?country=${country}&spedition=${speditions.join(
        ','
      )}${
        options?.search ? `&search=${encodeURIComponent(options.search)}` : ''
      }${
        options?.bounds ? `&bounds=${options.bounds.join(',')}` : ''
      }&access_token=${process.env['NEXT_PUBLIC_OLZA_API_KEY']}`,
      {},
      fetch
    );

    return result?.data?.items || [];
  } catch (e) {
    return [];
  }
}

export async function fetchPacketaClusters(
  country: string,
  zoom: number,
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): Promise<PacketaClusterItem[]> {
  try {
    const result = await storeClient<{
      clusters: PacketaClusterItem[];
    }>(
      `https://widget.packeta.com/v6/wps/api/widget/v2/clusters?Zoom=${zoom}&Lat1=${lat1}&Lon1=${lon1}&Lat2=${lat2}&Lon2=${lon2}&App_ApiKey=${process.env['NEXT_PUBLIC_PACKETA_API_KEY']}&App_Countries=${country}&User_SaturdayOpen=false&User_SundayOpen=false&User_PayByCard=false&User_WheelChairAccessible=false&User_AvailableCapacity=false`,
      {},
      fetch
    );

    return result?.clusters || [];
  } catch (e) {
    return [];
  }
}

export async function fetchPacketaPoint(
  externalId: string
): Promise<PacketaPointItem | null> {
  try {
    const result = await storeClient<PacketaPointItem>(
      `https://widget.packeta.com/v6/pps/api/widget/v2/${externalId}?App_ApiKey=${process.env['NEXT_PUBLIC_PACKETA_API_KEY']}`,
      {},
      fetch
    );

    return result || null;
  } catch (e) {
    return null;
  }
}

export async function fetchPacketaSearch(
  country: string,
  language: string,
  text: string
): Promise<PacketaSearch | null> {
  try {
    const result = await storeClient<PacketaSearch>(
      `https://packeta01.geoapify.net/v1/geocode/search?text=${text}&limit=1&lang=${language}&filter=countrycode:${country}&apiKey=047c239dee7e409aa6d72e91a3679547`,
      {},
      fetch
    );

    return result || null;
  } catch (e) {
    return null;
  }
}

export const getOrderSummary = async (
  orderId: string,
  fetch?: FetchClient
): Promise<null | OrderStatus> => {
  try {
    const result = await storeClient<OrderStatus>(
      `/order/summary/${orderId}`,
      {},
      fetch
    );

    const products = await Promise.all(
      result.items.map(async (item) => {
        return await fetchProduct(item.productSlug, fetch);
      })
    );

    return {
      ...result,
      items: result.items.map((item) => ({
        ...item,
        product: products.find(
          (product) => product?.productId === item.productId
        ),
      })),
    };
  } catch (e) {
    return null;
  }
};

export async function validateVatOSS(
  basketId: string,
  vatIdentity: string
): Promise<VatOSSCheck | null> {
  try {
    const result = await storeClient<VatOSSCheck>('/basket/vat-oss-check', {
      method: 'POST',
      body: {
        basketId,
        vatIdentity,
      },
    });

    return result;
  } catch {
    return null;
  }
}

export const fetchCartSeo = async (
  fetch?: FetchClient
): Promise<SeoResponse> => {
  try {
    const result = await storeClient<SeoResponse>('/basket/seo', {}, fetch);

    return result;
  } catch (e) {
    return {
      locale: '',
      canonical: '',
      robots: '',
      meta: {
        description: '',
        title: '',
        canonical: null,
      },
    };
  }
};

export const fetchCheckoutSeo = async (
  fetch?: FetchClient
): Promise<SeoResponse> => {
  try {
    const result = await storeClient<SeoResponse>('/order/seo', {}, fetch);

    return result;
  } catch (e) {
    return {
      locale: '',
      canonical: '',
      robots: '',
      meta: {
        description: '',
        title: '',
        canonical: null,
      },
    };
  }
};

export const fetchBasketCross = async (
  fetch?: FetchClient
): Promise<BasketCrossResponse> => {
  try {
    const result = await storeClient<BasketCrossResponse>(
      '/basket-cross-sell/get',
      {},
      fetch
    );

    return result;
  } catch (e) {
    return {
      name: '',
      products: [],
    };
  }
};

export async function fetchOpenStreetGeocodeSearch(
  value: string,
  searchBy: 'q' | 'postalcode',
  languageCode: string
): Promise<OpenStreetMapGeocodeResponse | null> {
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append('accept-language', languageCode);
  urlSearchParams.append('format', 'json');

  if (searchBy === 'postalcode') {
    urlSearchParams.append('country', languageCode);
    urlSearchParams.append('postalcode', value.replaceAll('-', ''));
  } else {
    urlSearchParams.append('q', value);
  }

  try {
    const result = await fetch(
      `https://nominatim.openstreetmap.org/search?${urlSearchParams.toString()}`,
      {
        next: {
          revalidate: 3600,
        },
      }
    ).then((res) => res.json());

    return result || null;
  } catch (e) {
    return null;
  }
}

export const fetchInpostPoints = async (
  body: PickupPointPayload,
  fetch?: FetchClient
): Promise<PickupPointResponse<InpostItem>> => {
  try {
    const result = await storeClient<PickupPointResponse<InpostItem>>(
      '/basket/inpost-pickup-points-list-grid',
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return result;
  } catch (e) {
    return {
      columns: [],
      count: 0,
      data: [],
    };
  }
};

export const fetchOrlenPaczkaPoints = async (
  body: PickupPointPayload,
  fetch?: FetchClient
): Promise<PickupPointResponse<OrlenPaczkaItem>> => {
  try {
    const result = await storeClient<PickupPointResponse<OrlenPaczkaItem>>(
      '/basket/orlen-paczka-pickup-points-list-grid',
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return result;
  } catch (e) {
    return {
      columns: [],
      count: 0,
      data: [],
    };
  }
};

export const fetchDhlPickupPointsByGeo = async (
  body: DhlPickupPointByGeoPayload
): Promise<DhlPickupPointResponse | null> => {
  try {
    const urlSearchParams = new URLSearchParams(
      Object.entries({
        ...body,
        limit: '50',
        serviceType: 'parcel:pick-up',
        radius: '1000000',
      })
    );
    const result = await fetch(
      `/api/dhl/location-finder/find-by-geo?${urlSearchParams.toString()}`,
      {
        method: 'GET',
      }
    ).then((res) => res.json());

    return result;
  } catch (e) {
    return null;
  }
};
